import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { BookOpen, Building2, GraduationCap, Settings, Users } from "lucide-react";

import type { ReportGranularity } from "../../../../shared/components/reports";
import SectionTabs from "../../../../shared/ui/tabs/SectionTabs";
import { toLocalDateKey } from "../../../../shared/utils/date";
import { useCenter, useCenterMembers } from "../../../center";
import CenterAdminShell from "../components/CenterAdminShell";
import CenterPayRatesCard from "../components/CenterPayRatesCard";
import CenterCourseReports from "../components/reports/CenterCourseReports";
import CenterOverviewReports from "../components/reports/CenterOverviewReports";
import CenterStudentReports from "../components/reports/CenterStudentReports";
import CenterTherapistReports from "../components/reports/CenterTherapistReports";

export type ReportSection = "center" | "therapists" | "students" | "courses" | "settings";

const SECTIONS: ReportSection[] = ["center", "therapists", "students", "courses", "settings"];
const PANEL_ID = "center-reports-panel";

function isoDay(offsetDays: number): string {
    const day = new Date();
    day.setDate(day.getDate() + offsetDays);
    return toLocalDateKey(day);
}

const DEFAULT_GRANULARITY: ReportGranularity = "day";

/**
 * What the practice did, in three places: the centre as a whole, its
 * professionals one by one, and how the reports are set up — the rates the
 * centre pays, if it pays any.
 *
 * The section lives in the address (`?section=`), so a link can open the one
 * it means. The dates are shared by the two report sections: moving between
 * them keeps the window the administrator chose.
 */
export default function CenterReportsPage() {
    const { t } = useTranslation();
    const { center, loading } = useCenter();
    const { members } = useCenterMembers(center?.id);
    const [params, setParams] = useSearchParams();
    const requested = params.get("section") as ReportSection | null;
    const training = center?.type === "training";
    // Courses are a training centre's; anywhere else the section does not exist.
    const available = SECTIONS.filter((id) => id !== "courses" || training);
    const section: ReportSection = requested && available.includes(requested) ? requested : "center";

    const [from, setFrom] = useState(isoDay(-30));
    const [to, setTo] = useState(isoDay(0));
    const [granularity, setGranularity] = useState<ReportGranularity>(DEFAULT_GRANULARITY);
    const [therapistId, setTherapistId] = useState("");

    const selectSection = (next: ReportSection) =>
        setParams(next === "center" ? {} : { section: next }, { replace: true });

    const resetFilters = () => {
        setFrom(isoDay(-30));
        setTo(isoDay(0));
        setGranularity(DEFAULT_GRANULARITY);
        setTherapistId("");
    };

    if (loading || !center) {
        return <p className="p-6 text-sm text-content-secondary">{t("common.loading")}</p>;
    }

    const dates = { from, to, onFromChange: setFrom, onToChange: setTo, onClear: resetFilters };

    return (
        <CenterAdminShell center={center}>
            <div>
                <h2 className="mb-1 text-lg font-semibold text-content-primary">{t("center.reports.title")}</h2>
                <p className="mb-4 text-sm text-content-secondary">{t("center.reports.subtitle")}</p>
                <SectionTabs
                    label={t("center.reports.sections.label")}
                    className="mb-0"
                    activeTab={section}
                    onChange={selectSection}
                    tabs={[
                        { id: "center" as const, label: t("center.reports.sections.center"), icon: Building2 },
                        { id: "therapists" as const, label: t("center.reports.sections.therapists"), icon: Users },
                        { id: "students" as const, label: t("center.reports.sections.students"), icon: GraduationCap },
                        { id: "courses" as const, label: t("center.reports.sections.courses"), icon: BookOpen },
                        { id: "settings" as const, label: t("center.reports.sections.settings"), icon: Settings },
                    ].filter((tab) => available.includes(tab.id))}
                />
            </div>

            <div
                id={PANEL_ID}
                role="tabpanel"
                aria-label={t(`center.reports.sections.${section}`)}
                className="flex flex-col gap-6"
            >
                {section === "center" && (
                    <CenterOverviewReports
                        centerId={center.id}
                        {...dates}
                        granularity={granularity}
                        onGranularityChange={setGranularity}
                    />
                )}
                {section === "therapists" && (
                    <CenterTherapistReports
                        centerId={center.id}
                        {...dates}
                        therapistId={therapistId}
                        onTherapistChange={setTherapistId}
                    />
                )}
                {section === "students" && <CenterStudentReports centerId={center.id} {...dates} />}
                {section === "courses" && <CenterCourseReports centerId={center.id} {...dates} />}
                {section === "settings" && (
                    <CenterPayRatesCard
                        centerId={center.id}
                        members={members.filter((m) => m.status === "active" && m.is_practising_therapist)}
                    />
                )}
            </div>
        </CenterAdminShell>
    );
}
